import { Head, usePage } from '@inertiajs/react';

import type { SeoMeta } from '@/Types/seo';

type SharedProps = {
    meta: SeoMeta;
};

/**
 * Emits the canonical `<head>` block — title, meta description, canonical
 * link, robots, OG, and hreflang. Reads from the shared `meta` Inertia prop
 * populated by App\Services\Seo\MetaBuilder.
 *
 * Drops into every page once. Pages are free to render additional `<Head>`
 * children alongside this (e.g., page-specific theme-color) without
 * conflicting — Inertia merges the children server-side.
 */
export function MetaHead() {
    const { meta } = usePage<SharedProps>().props;
    if (!meta) {
        return null;
    }

    return (
        <Head>
            <title>{meta.title}</title>
            <meta name="description" content={meta.description} />
            <meta name="robots" content={meta.robots} />
            {meta.canonical ? <link rel="canonical" href={meta.canonical} /> : null}

            <meta property="og:title" content={meta.title} />
            <meta property="og:description" content={meta.description} />
            <meta property="og:type" content={meta.og.type} />
            <meta property="og:site_name" content={meta.og.site_name} />
            {meta.canonical ? <meta property="og:url" content={meta.canonical} /> : null}
            {meta.og.image ? <meta property="og:image" content={meta.og.image} /> : null}

            <meta name="twitter:card" content="summary_large_image" />

            {meta.hreflang.map((h) => (
                <link
                    key={`${h.hreflang}-${h.href}`}
                    rel="alternate"
                    hrefLang={h.hreflang}
                    href={h.href}
                />
            ))}
        </Head>
    );
}
